/* eslint-disable lingui/no-unlocalized-strings */
import { EMPTY_STRING, localStorageGetTyped, RegrelloLocalStorageKey } from "@regrello/core-utils";
import type { UserVerificationStatus, VerifyUserIdentityResponse, WorkspaceSetting } from "@regrello/graphql-api";
import { HttpStatusCode } from "../constants/HttpStatusCodes";
import { RouteQueryStringKeys } from "../ui2/app/routes/consts";
import { isDevServer } from "../utils/environmentUtils";

/**
 * A service for communicating with the Regrello backend via REST. Typically used for GET'ing
 * peripheral metadata that doesn't make sense to surface via the graph-modeled GraphQL API.
 */
export const RegrelloRestApiService = {
  getUserVerificationStatus: () => executeAuthenticatedGetRequest("/user/verification-status"),
  verifyUserIdentity: (authUserEmail: string | null, uuid: string | null, forceVerifyForExistingUser: boolean | null) =>
    executePostRequest(
      "/user/verify",
      { AuthUserEmail: authUserEmail, UUID: uuid, ForceVerifyForExistingUser: forceVerifyForExistingUser },
      { accessToken: getAuthToken(), includeCredentials: true },
    ),
  getFeatureFlags: (workspaceShortUuid: string | null) =>
    executeGetRequestWithQueryParams(
      "/feature-flags",
      workspaceShortUuid != null ? { workspace: workspaceShortUuid } : {},
    ),
  getLogo: (tenantUuid: string) => executeGetRequestWithQueryParams("/workspace-logo", { "tenant-uuid": tenantUuid }),
  getToken: (code: string, email: string) =>
    executePostRequest("/auth/request-for-token-with-code", { code: code, email: email }, { includeCredentials: true }),
  refreshToken: (token: string) => executePostRequest("/auth/request-for-token-with-token", { access_token: token }),
  logout: (token: string) => executePostRequest("/auth/logout", { access_token: token }),
  getDevToken: (username: string) => executePostRequest("/dev/login-as", { username }),
  isSso: (emailDomain: string) => executePostRequest("/is-sso", { domain: emailDomain }),
  reportFpsPerformance: ({ userEmail, tenantName, fps }: { userEmail: string; tenantName: string; fps: number }) =>
    executePostRequest(
      "/fe-slowdown-reports",
      { user: userEmail, workspace: tenantName, fps },
      { discardResponse: true },
    ),
  resendInvite: (email: string) => executePostRequest("/resend-invite", { Email: email }),
  requestVerificationLink: (granularAccessToken: string, email: string | undefined | null) =>
    executeGetRequestWithQueryParams("/verification/request", {
      token: granularAccessToken,
      ...(email != null ? { email: email } : {}),
    }),
  exchangeVerificationLink: (token: string, nonce: string, neededConfirmation: boolean) =>
    executePostRequest(
      "/verification/exchange",
      {
        token,
        nonce,
        neededConfirmation,
      },
      {
        includeCredentials: true,
      },
    ),
  getWorkspacePersonalization: (granularAccessToken: string) =>
    executeGetRequestWithQueryParams("/workspace-personalization", {
      token: granularAccessToken,
    }),
  sendMfaResetRequestEmail: (email: string) =>
    executePostRequest("/send-mfa-reset-request-email", {
      email,
    }),
  resetMfa: (email: string, verificationUUID: string, timestamp: string) =>
    executePostRequest("/reset-mfa", {
      email,
      verificationUUID,
      timestamp,
    }),
  resetPassword: (email: string, nonce: string, password: string) =>
    executePostRequest("/auth/reset-password", {
      email,
      nonce,
      password,
    }),
};

// This should match the EnvironmentResponse payload in handlers.go.
//
// TODO (clewis, dhanson): It would be better to derive our REST response types from our server API
// via Swagger or Conjure. Right now, we have to manually keep types on both sides in sync.
//
// See: https://swagger.io/, https://palantir.github.io/conjure/
type RegrelloRestApi = {
  "/feature-flags": Record<string, boolean>;
  "/is-sso": {
    isSso: boolean;
    isSsoOptional: boolean;
    connectionName: string;
  };
  "/resend-invite": {
    Success: string;
    Error: string | undefined;
  };
  "/auth/request-for-token-with-code": {
    accessToken: string;
    expireAt: string;
  };
  "/auth/request-for-token-with-token": {
    accessToken: string;
    expireAt: string;
  };
  "/auth/logout": {
    accessToken: string;
  };
  "/dev/login-as": {
    authUserId: number;
    tenantUserId: number;
    token: string;
    error: string;
  };
  "/verification/request": Record<string, never>;
  "/verification/exchange": {
    redirect: string;
  };
  "/workspace-personalization": {
    Logo: WorkspaceSetting | undefined;
  };
  "/fe-slowdown-reports": undefined;
  "/user/verification-status": UserVerificationStatus;
  "/user/verify": VerifyUserIdentityResponse;
  "/workspace-logo": {
    Logo: WorkspaceSetting | undefined;
  };
  "/send-mfa-reset-request-email": {
    sent: boolean;
    reason: string;
  };
  "/reset-mfa": {
    reset: boolean;
    reason: string;
  };
  "/auth/reset-password": {
    success: boolean;
  };
};

export class HttpError extends Error {
  public status: HttpStatusCode;

  public responseBody: unknown;

  constructor(message: string, status: number, responseBody: unknown) {
    super(message);
    this.status = status;
    this.responseBody = responseBody;
    Object.setPrototypeOf(this, HttpError.prototype);
  }
}

// TODO(Elle) refactor http handlers
function executeGetRequestBase<TEndpoint extends keyof RegrelloRestApi>(
  endpoint: TEndpoint,
  queryParams?: Record<string, string> | null,
  accessToken?: string,
  signal?: AbortSignal,
): Promise<Response> {
  const urlParts = new URLSearchParams();
  if (queryParams != null) {
    Object.entries(queryParams).forEach(([key, val]) => {
      urlParts.append(key, val);
    });
  }

  const headers = new Headers();
  if (accessToken) {
    headers.append("Authorization", `Bearer ${accessToken}`);
  }

  return fetch(`${config.ApiHostname}${endpoint}?${urlParts.toString()}`, {
    method: "GET",
    headers: headers,
    // (elle): include cookie in the header for device verification
    credentials: "include",
    signal: signal,
  });
}

async function executeAuthenticatedGetRequest<
  TEndpoint extends keyof RegrelloRestApi,
  TResponse extends RegrelloRestApi[TEndpoint],
>(endpoint: TEndpoint, queryParams?: Record<string, string> | null): Promise<{ status: number; json: TResponse }> {
  const accessToken = getAuthToken();
  if (!accessToken) {
    throw new Error("No authentication token available for the user");
  }

  const response = await executeGetRequestBase(endpoint, queryParams, accessToken);

  const responseBody = await parseResponseBody(response);

  if (response.status !== HttpStatusCode.Ok) {
    throw new HttpError("Request failed", response.status, responseBody);
  }

  return {
    status: response.status,
    json: responseBody,
  };
}

async function executeGetRequestWithQueryParams<
  TEndpoint extends keyof RegrelloRestApi,
  TResponse extends RegrelloRestApi[TEndpoint],
>(endpoint: TEndpoint, queryParams: Record<string, string>): Promise<{ status: number; json: TResponse }> {
  const response = await executeGetRequestBase(endpoint, queryParams);
  const responseBody = await parseResponseBody(response);

  return {
    status: response.status,
    json: responseBody,
  };
}

/**
 * Executes a `POST` HTTP request against the backend.
 *
 * @param endpoint - The backend path to request against.
 * @param body - Body to supply as a JSON payload in the request body.
 * @param options - Configuration for how the response is handled.
 * @returns The backend's reply.
 */
async function executePostRequest<
  TEndpoint extends keyof RegrelloRestApi,
  TResponse extends RegrelloRestApi[TEndpoint],
>(
  endpoint: TEndpoint,
  body: Record<string, string | number | boolean | null>,
  options?: {
    /** Whether the response body should be ignored entirely. */
    discardResponse?: boolean;
    accessToken?: string | null;
    includeCredentials?: boolean;
  },
): Promise<{ status: number; json: TResponse }> {
  const headers = new Headers({
    "Content-Type": "application/json",
  });

  if (options?.accessToken != null) {
    headers.append("Authorization", `Bearer ${options.accessToken}`);
  }

  const response = await fetch(`${config.ApiHostname}${endpoint}`, {
    headers: headers,
    method: "POST",
    body: JSON.stringify(body),
    // (elle): include cookie in the header for device verification
    credentials: options?.includeCredentials === true ? "include" : undefined,
  });
  return {
    status: response.status,
    json: options?.discardResponse ? undefined : await response.json(),
  };
}

// Retrieves the authentication token from storage
function getAuthToken(): string | null {
  // Fetch the user's authentication token, which is required to get data from our backend.
  const spoofingUser = isDevServer() ? localStorageGetTyped(RegrelloLocalStorageKey.SPOOFING_USER_INFO) : undefined;

  // Find token from spoofing user
  if (spoofingUser?.token != null) {
    return spoofingUser.token;
  }

  // Find token from local storage key for the Regrello website
  const auth0UserV2 = localStorageGetTyped(RegrelloLocalStorageKey.AUTH_0_USER_KEY);

  if (auth0UserV2?.token != null) {
    return auth0UserV2?.token;
  }

  // Find token from URL for webform
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get(RouteQueryStringKeys.TOKEN);
  if (token != null) {
    return token;
  }

  // No token found
  return EMPTY_STRING;
}

// Attempt to parse the response body as JSON, fallback to plain text if parsing fails
async function parseResponseBody(response: Response) {
  const contentType = response.headers.get("Content-Type") || "";

  if (contentType.includes("application/json")) {
    return response.json();
  }
  return response.text();
}
